import {
  type SelectionsActions,
  type SelectedValueCoordinate,
  type SelectionIntent,
  type SelectionMutationOptions
} from '@databases/selections-api-types';

import { type SelectionsApi } from '../../selections-api.ts';

export interface SelectionsApiAdapterOptions {
  selectionsApi: SelectionsApi;
}

/**
 * Adapter that implements SelectionsActions using the new SelectionsApi.
 *
 * This adapter wraps the SelectionsApi to provide the same interface as the
 * legacy Redux-based adapter, enabling gradual migration without breaking changes.
 *
 * Note: Some methods are currently no-ops because the underlying SelectionsApi
 * doesn't yet support those operations. These will be implemented as the
 * SelectionsApi evolves.
 */
export class SelectionsApiAdapter implements SelectionsActions {
  #api: SelectionsApi;

  constructor(options: SelectionsApiAdapterOptions) {
    this.#api = options.selectionsApi;
  }

  selectFields(fieldIds: string[], _options: SelectionMutationOptions = {}): void {
    this.#api.selectFields(fieldIds);
  }

  selectEntries(entryIds: string[], _options: SelectionMutationOptions = {}): void {
    this.#api.selectEntries(entryIds);
  }

  selectValues(
    values: SelectedValueCoordinate[],
    _options: SelectionMutationOptions = {}
  ): void {
    this.#api.selectValues(values);
  }

  deselectAll(): void {
    this.#api.deselectAll();
  }

  setSelectionIntent(intent: SelectionIntent): void {
    if (intent.type === 'delete') {
      this.#api.previewDeleteIntent();
    } else if (intent.type === 'softSelection') {
      // TODO: SelectionsApi doesn't currently support soft selection
      // This is a UI-only concern that may be handled differently in the new system
      // For now, this is a no-op
    }
  }

  clearSelectionIntent(): void {
    this.#api.clearPreviewIntent();
  }

  setFirstSelectedValue(_value: SelectedValueCoordinate | null): void {
    // The new SelectionsApi handles range selection tracking internally.
    // This is a no-op as the machine manages selection anchor state.
  }
}
